import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SCOPE_WILDCARD, masterRoleId } from '../../lib/constants';
import { updateUser } from '../../lib/identityRegistry';
import type {
  AssignedInstance,
  FacilityRecord,
  RoleRecord,
  UserRecord,
  UserUpdatePayload,
} from '../../types/registry';
import { AssignFacilityDialog } from './AssignFacilityDialog';

const ALIAS_MAX_LENGTH = 64;

interface UserEditDrawerProps {
  user: UserRecord;
  roles: RoleRecord[];
  facilities: FacilityRecord[];
  onClose: () => void;
}

function areaSummary(facility: FacilityRecord | undefined, areaIds: string[]): string {
  if (areaIds.includes(SCOPE_WILDCARD)) return 'All areas';
  const names = (facility?.area_ids ?? [])
    .filter((area) => areaIds.includes(area.area_id))
    .map((area) => area.name);
  return names.length ? names.join(', ') : 'All areas';
}

export function UserEditDrawer({
  user,
  roles,
  facilities,
  onClose,
}: UserEditDrawerProps): JSX.Element {
  const queryClient = useQueryClient();
  // Only the per-client master role is non-assignable; all other roles (incl. viewer) are allowed.
  const assignableRoles = useMemo(
    () => roles.filter((entry) => entry.role_id !== masterRoleId(entry.client_id)),
    [roles],
  );
  const isMasterUser = user.role === masterRoleId(user.client_id);
  const [role, setRole] = useState(user.role);
  const [alias, setAlias] = useState(user.alias ?? '');
  const [selectedFacilities, setSelectedFacilities] = useState<Record<string, string[]>>(() => {
    const map: Record<string, string[]> = {};
    for (const instance of user.assigned_instances ?? []) {
      map[instance.facility_id] = [...(instance.area_ids ?? [])];
    }
    return map;
  });
  const [addingFacility, setAddingFacility] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: UserUpdatePayload) => updateUser(user.user_id, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (mutationError) => {
      setError(mutationError instanceof Error ? mutationError.message : 'Update failed');
    },
  });

  const assignedInstances = useMemo<AssignedInstance[]>(() => {
    return Object.entries(selectedFacilities).map(([facilityId, areaIds]) => ({
      facility_id: facilityId,
      area_ids: areaIds.length ? areaIds : [SCOPE_WILDCARD],
    }));
  }, [selectedFacilities]);

  function removeFacility(facilityId: string) {
    setSelectedFacilities((current) => {
      const next = { ...current };
      delete next[facilityId];
      return next;
    });
  }

  function handleMasterSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate({ alias: alias.trim() });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate({
      role,
      assigned_instances: assignedInstances,
      alias: alias.trim(),
    });
  }

  const aliasField = (
    <label className="form-field">
      <span>Display name</span>
      <input
        type="text"
        maxLength={ALIAS_MAX_LENGTH}
        value={alias}
        onChange={(event) => setAlias(event.target.value)}
        autoComplete="nickname"
        placeholder="Optional"
      />
    </label>
  );

  if (isMasterUser) {
    return (
      <div className="drawer-backdrop" onClick={onClose}>
        <aside className="drawer" onClick={(event) => event.stopPropagation()}>
          <h2>Edit user</h2>
          <p className="panel__copy">{user.email ?? user.user_id}</p>
          <p className="hint-text">
            This is the account master user. Only the display name can be changed here; role and
            scope are locked.
          </p>

          <form className="invite-form" onSubmit={handleMasterSubmit}>
            {aliasField}
            {error ? <div className="form-error">{error}</div> : null}
            <div className="drawer__actions">
              <button className="primary-button" disabled={mutation.isPending} type="submit">
                {mutation.isPending ? 'Saving...' : 'Save display name'}
              </button>
              <button className="ghost-button" type="button" onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>
        </aside>
      </div>
    );
  }

  const assignedEntries = Object.entries(selectedFacilities);

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose}>
        <aside className="drawer" onClick={(event) => event.stopPropagation()}>
          <h2>Edit user</h2>
          <p className="panel__copy">{user.email ?? user.user_id}</p>

          <form className="invite-form" onSubmit={handleSubmit}>
            {aliasField}

            <label className="form-field">
              <span>Role</span>
              <select value={role} onChange={(event) => setRole(event.target.value)} required>
                {assignableRoles.map((entry) => (
                  <option key={entry.role_id} value={entry.role_id}>
                    {entry.role_name}
                  </option>
                ))}
              </select>
            </label>

            <div className="facility-picker">
              <span>Facility scope</span>
              {!assignedEntries.length ? (
                <p className="hint-text">No facilities assigned yet.</p>
              ) : null}
              {assignedEntries.map(([facilityId, areaIds]) => {
                const facility = facilities.find((entry) => entry.facility_id === facilityId);
                return (
                  <div key={facilityId} className="facility-block">
                    <p className="facility-block__title">{facility?.name ?? facilityId}</p>
                    <p className="hint-text">{areaSummary(facility, areaIds)}</p>
                    <button
                      className="ghost-button"
                      type="button"
                      onClick={() => removeFacility(facilityId)}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
              <button
                className="secondary-button"
                type="button"
                onClick={() => setAddingFacility(true)}
              >
                Add facility
              </button>
            </div>

            {error ? <div className="form-error">{error}</div> : null}

            <div className="drawer__actions">
              <button className="primary-button" disabled={mutation.isPending} type="submit">
                {mutation.isPending ? 'Saving...' : 'Save changes'}
              </button>
              <button className="ghost-button" type="button" onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>
        </aside>
      </div>

      {addingFacility ? (
        <AssignFacilityDialog
          facilities={facilities}
          assignedFacilityIds={Object.keys(selectedFacilities)}
          onAdd={(facilityId, areaIds) => {
            setSelectedFacilities((current) => ({ ...current, [facilityId]: areaIds }));
            setAddingFacility(false);
          }}
          onCancel={() => setAddingFacility(false)}
        />
      ) : null}
    </>
  );
}
